import * as Sentry from '@sentry/react';
import * as uuid from 'uuid';
import { ICState } from 'schema/state';
import StackTracey from 'stacktracey';

class MyStackTracey extends StackTracey {
  override isThirdParty(path: string): boolean {
    return super.isThirdParty(path) || path.includes('.pnpm');
  }
}

const breadcrumbs: unknown[] = [];

type Breadcrumb =
  | { level: 'debug'; category: 'ICState'; data: ICState }
  | { level: 'debug'; category: 'pollsIC'; data: { pollsIc: string } }
  | { level: 'debug'; category: 'logs'; data: { logs: unknown[] } }
  | { level: 'debug'; category: 'auth'; data?: Record<string, unknown> };

export function addBreadcrumb(breadcrumb: Breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb);
  breadcrumbs.push(breadcrumb);
}

const isProd = process.env.NODE_ENV === 'production';
const dsn = isProd ? window.APP_SENTRY_DSN : window.APP_SENTRY_DSN ?? 'https://test@test.test/3';

const config: Parameters<typeof Sentry.init>[0] = {
  dsn,
  environment: isProd ? 'production' : 'development',
  release: `components@${process.env.__APP_VERSION__ ?? '0.0.1+1111'}`,
  async beforeSend(event, hint) {
    const rawStack = hint.originalException instanceof Error && hint.originalException.stack;
    if (rawStack) {
      const parsedStack = new MyStackTracey(rawStack);
      try {
        const sourceStack = await parsedStack.withSourcesAsync();
        const header = rawStack.split('\n')[0]!;
        const remappedStack = [
          header,
          ...sourceStack.items.map(
            i =>
              `at ${i.callee} (${i.file
                .replace(/.*components\/src/, '')
                .replace(/.*monorepo\//, '')
                .replace(/\?$/, '')}:${i.line ?? 'N/A'}:${i.column ?? 'N/A'})`,
          ),
        ].join('\n');
        event.message = (event.message ?? '') + remappedStack;
      } catch (e) {
        console.error('FAILED TO PARSE', e);
      }
    }
    return event;
  },
  enabled: !!dsn,
  integrations: [new Sentry.Integrations.Breadcrumbs({ console: false })],
};

export type SentryEventOptions = {
  actualError?: unknown;
  severity?: 'MINOR' | 'MAJOR' | 'CRITICAL';
  additionalInfo?: Record<string, string | number | undefined | null>;
};

function sendMessage(
  level: 'error' | 'info',
  message: string,
  { actualError, additionalInfo, severity = 'MINOR' }: SentryEventOptions = {},
) {
  if (!dsn) {
    return;
  }

  const eventId = uuid.v4();

  const [, sentry_key, sentry_domain, id]: [string, string, string, string] = (dsn.match(
    /^https:\/\/(.*)@(.*)\/(\d+)$/,
  ) as [string, string, string, string] | null) ?? ['', '', '', ''];

  return [
    eventId,
    fetch(
      `https://${sentry_domain}/api/${id}/envelope/?sentry_key=${sentry_key}&sentry_version=1&sentry_client=custom`,
      {
        body: [
          {
            event_id: eventId,
            sent_at: new Date().toISOString(),
            sdk: { name: 'custom', version: '1.0.0' },
            trace: {
              environment: config.environment,
              release: config.release,
              public_key: 'cad_polls',
            },
          },
          { type: 'event' },
          {
            level,
            event_id: eventId,
            platform: 'javascript',
            timestamp: new Date().valueOf() / 1000,
            environment: config.environment,
            release: config.release,
            sdk: {
              integrations: [],
              name: 'custom',
              version: '1.0.0',
              packages: [],
            },
            breadcrumbs,
            request: {
              url: window.location.toString(),
              headers: {
                'User-Agent': window.navigator.userAgent,
              },
            },
            extra: {
              ...additionalInfo,
              ...(actualError ? { actualError: stringifyError(actualError) } : undefined),
              ...getPollMetadata(),
            },
            message,
            tags: { severity },
          },
        ]
          .map(v => JSON.stringify(v))
          .join('\n'),
        method: 'POST',
        credentials: 'omit',
      },
    ),
  ] as const;
}

export function sendError(error: Error, options?: SentryEventOptions) {
  console.error(error);
  return sendMessage('error', stringifyError(error), options);
}

export function sendInfo(message: string, options?: SentryEventOptions) {
  // eslint-disable-next-line no-console
  console.info(message);
  return sendMessage('info', message, options);
}

Sentry.init(config);

function getPollMetadata(): { revision?: number; wave?: number } {
  try {
    const poll = window.__pollData;
    return { revision: poll?.revision ?? 1, wave: poll?.wave ?? 1 };
  } catch (error) {
    console.error(error);
    return {};
  }
}

function stringifyError(error: unknown) {
  if (error instanceof Error) {
    return `${error.name}\n${error.message}\n${error.stack ?? '<unknown stack>'}`;
  } else {
    try {
      return JSON.stringify(error);
    } catch (error) {
      console.error(error);
      return '<unknown error>';
    }
  }
}
